import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import {
  campaignSale,
  offerApproval,
  offerVersionEndpoints,
  offers,
  publicOfferLink,
} from '@ooh/contracts';
import type { EndpointDef, Offer, UpdateOffer, callEndpoint } from '@ooh/contracts';
import { listVersionsOptions, offerKeys, offerPublicLinkKeys, offerQueries } from './queries';
import type { WithCallWithAuth } from './queries';
import { useHasPermission } from '../../../hooks/use-has-permission';
import { RESERVATION_LIST_KEY_PREFIX, allOfferVersionsQueryKey } from '@ooh/screens';

interface MutationAuth extends WithCallWithAuth {
  callWithAuth: <TDef extends EndpointDef>(
    def: TDef,
    request: Parameters<typeof callEndpoint<TDef>>[1],
  ) => ReturnType<typeof callEndpoint<TDef>>;
}

export interface UpdateOfferVars extends UpdateOffer {
  id: string;
}

export interface DeleteOfferVars {
  id: string;
}

export interface ApproveOfferVars {
  id: string;
}

export interface MarkOfferSoldVars {
  id: string;
  /** Whole-call opt-in to include every not-currently-accepted nośnik (campaign-sale). */
  includeNotCurrentlyAccepted?: boolean;
}

export interface SetOfferStatusVars {
  id: string;
  reason: string;
  targetStatus: Offer['status'];
}

export interface CancelOfferVars {
  id: string;
  reason: string;
}

export interface UpdateCampaignNumberVars {
  id: string;
  campaignNumber: string;
  updatedAt: Date;
}

function useEffectiveAuth(): MutationAuth {
  const { auth } = useRouteContext({ from: '__root__' });
  // RouterContext['auth'] is structurally identical to MutationAuth but TS cannot
  // infer the narrowing from useRouteContext's return type — cast is safe.
  return auth as MutationAuth;
}

async function invalidateOfferDetail(
  queryClient: ReturnType<typeof useQueryClient>,
  offerId: string,
) {
  await queryClient.invalidateQueries({
    queryKey: offerKeys.detail(offerId),
    exact: true,
  });
}

async function invalidateAllOfferLists(queryClient: ReturnType<typeof useQueryClient>) {
  // Use the 'offers' prefix to invalidate all list views (mine, shared-with-me, all) at once.
  await queryClient.invalidateQueries({ queryKey: ['offers'] });
}

async function invalidateOfferCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  offerId: string,
) {
  await Promise.all([
    invalidateOfferDetail(queryClient, offerId),
    invalidateAllOfferLists(queryClient),
  ]);
}

/**
 * Write the returned offer directly into the cache so the UI reflects the
 * new status immediately without waiting for the background refetch to
 * complete. invalidateOfferCaches still runs to keep list views fresh.
 */
async function writeAndInvalidateOfferCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
  data: Offer,
) {
  queryClient.setQueryData(offerKeys.detail(id), data);
  await invalidateOfferCaches(queryClient, id);
}

/**
 * Populates the offers-list "assigned to" picker. Self-gates on offer.view_all.
 * The server enforces the same check, but this avoids an unnecessary 403 request
 * for callers who cannot use the picker anyway.
 */
export function useOfferAssignableUsers() {
  const auth = useEffectiveAuth();
  const canManageAny = useHasPermission('offer.view_all');
  return useQuery({ ...offerQueries.assignableUsers(auth), enabled: canManageAny });
}

/**
 * Populates the create-offer and quick-add-offer-picker salesperson pickers.
 * Self-gates on offer.prepare (the same permission the endpoint itself requires)
 * so pages that call this unconditionally (Map, MediaUnits list/detail) don't
 * fire the request for callers who can't use any salesperson picker.
 * The server enforces the same check independently.
 */
export function useOfferSalesUsers() {
  const auth = useEffectiveAuth();
  const canUseSalespersonPicker = useHasPermission('offer.prepare');
  return useQuery({ ...offerQueries.salesUsers(auth), enabled: canUseSalespersonPicker });
}

export function useOffer(offerId: string) {
  const auth = useEffectiveAuth();
  return useQuery(offerQueries.detail(auth, offerId));
}

export function useUpdateOffer() {
  const auth = useEffectiveAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }: UpdateOfferVars) =>
      auth.callWithAuth(offers.update, { id: Number(id), ...body }),
    onSuccess: async (_data, { id }) => {
      await invalidateOfferCaches(queryClient, id);
    },
    // 409 conflict invalidation is handled by OfferHeader.handleConflict to avoid
    // double-invalidation. Non-409 errors need no query invalidation.
  });
}

export function useApproveOffer() {
  const auth = useEffectiveAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: ApproveOfferVars) =>
      auth.callWithAuth(offerApproval.approve, { id: Number(id) }),
    onError: () => {
      // handled by the caller for status-specific messaging
    },
    onSuccess: async (data, { id }) => {
      await writeAndInvalidateOfferCaches(queryClient, id, data);
    },
  });
}

/**
 * POST /offers/:id/mark-sold (campaign-sale / "Campaign sale endpoint") —
 * mirrors useApproveOffer 1:1: same auth/invalidation shape, only the
 * endpoint differs.
 */
export function useMarkOfferSold() {
  const auth = useEffectiveAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, includeNotCurrentlyAccepted }: MarkOfferSoldVars) =>
      auth.callWithAuth(campaignSale.markSold, { id: Number(id), includeNotCurrentlyAccepted }),
    onError: () => {
      // handled by the caller for status-specific messaging
    },
    onSuccess: async (data, { id }) => {
      await writeAndInvalidateOfferCaches(queryClient, id, data);
    },
  });
}

export function useSetOfferStatus() {
  const auth = useEffectiveAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason, targetStatus }: SetOfferStatusVars) =>
      auth.callWithAuth(offerApproval.setStatus, {
        id: Number(id),
        reason,
        targetStatus,
      }),
    onError: () => {
      // handled by the caller for status-specific messaging
    },
    onSuccess: async (data, { id }) => {
      await writeAndInvalidateOfferCaches(queryClient, id, data);
    },
  });
}

/**
 * POST /offers/:id/cancel — behind the offer's existing edit-access check
 * (no new permission code). Cancelling never touches reservations.
 */
export function useCancelOffer() {
  const auth = useEffectiveAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: CancelOfferVars) =>
      auth.callWithAuth(offers.cancel, { id: Number(id), reason }),
    onSuccess: async (data, { id }) => {
      await writeAndInvalidateOfferCaches(queryClient, id, data);
    },
    // Errors handled at the call site for status-specific messaging.
  });
}

/**
 * PATCH /offers/:id/campaign-number — manual override of an already-allocated
 * number, behind the same offer edit-access check. The general offer PATCH
 * still rejects this field (CAMPAIGN_NUMBER_IMMUTABLE) — this is the only
 * writable path.
 */
export function useUpdateCampaignNumber() {
  const auth = useEffectiveAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, campaignNumber, updatedAt }: UpdateCampaignNumberVars) =>
      auth.callWithAuth(offers.updateCampaignNumber, { id: Number(id), campaignNumber, updatedAt }),
    onSuccess: async (data, { id }) => {
      await writeAndInvalidateOfferCaches(queryClient, id, data);
    },
    // Errors handled at the call site for status-specific messaging.
  });
}

export function useDeleteOffer() {
  const auth = useEffectiveAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: DeleteOfferVars) => auth.callWithAuth(offers.delete, { id: Number(id) }),
    onSuccess: async () => {
      await invalidateAllOfferLists(queryClient);
    },
  });
}

export interface WithdrawAcceptanceBulkVars {
  versions: Array<{ offerId: number; versionId: number }>;
  reason?: string;
}

/**
 * Internal staff bulk action: POST /offers/versions/withdraw-acceptance-bulk.
 * Per-entry outcomes (withdrawn/skipped/failed) are reported in the response,
 * not thrown — the promise only rejects on a whole-request failure (e.g. an
 * over-cap payload). Invalidates the offer list, offer detail, versions list
 * and reservations queries broadly so both the offer list and the
 * reservations area reflect the change without a manual reload.
 */
export function useWithdrawAcceptanceBulk() {
  const auth = useEffectiveAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: WithdrawAcceptanceBulkVars) =>
      auth.callWithAuth(offerVersionEndpoints.withdrawAcceptanceBulk, vars),
    onSuccess: async () => {
      await Promise.all([
        invalidateAllOfferLists(queryClient),
        queryClient.invalidateQueries({ queryKey: allOfferVersionsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: RESERVATION_LIST_KEY_PREFIX }),
      ]);
    },
  });
}

export function usePublicLinkStatus(offerId: string) {
  const auth = useEffectiveAuth();
  return useQuery({
    queryKey: offerPublicLinkKeys.status(offerId),
    queryFn: () => auth.callWithAuth(publicOfferLink.linkStatus, { id: Number(offerId) }),
    enabled: !!offerId,
  });
}

export function useCreatePublicLink(offerId: string) {
  const auth = useEffectiveAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => auth.callWithAuth(publicOfferLink.createLink, { id: Number(offerId) }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: offerPublicLinkKeys.status(offerId) }),
        // Generating the first link transitions the offer DRAFT -> SENT server-side
        // (CU-86caypx5r) — refetch the offer detail so offer-workspace-header reflects
        // it without a manual reload.
        invalidateOfferDetail(queryClient, offerId),
      ]);
    },
  });
}

export function useRevokePublicLink(offerId: string) {
  const auth = useEffectiveAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => auth.callWithAuth(publicOfferLink.revokeLink, { id: Number(offerId) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: offerPublicLinkKeys.status(offerId) });
    },
  });
}

/**
 * Cheap client-side signal for "has this offer ever been published?" — mirrors the
 * server-side check in OfferPublicLinksService.generateLink (findLatestActiveVersion):
 * any existing version row implies a PUBLISHED/ACCEPTED version exists. Reusing the
 * versions-list query (rather than offer.status) matters because an admin can revert
 * offer.status back to DRAFT (offer-approval "Client retracted approval" flow) without
 * deleting the already-published version rows — offer.status alone would report a false
 * "not published" here. Gates the client-share dialog's link-generation and preview
 * affordances so users see a disabled control with an inline reason instead of a
 * dismissible-toast-only 422 (CU-86cak25ct). Exposes `isUnknownPublishStatus` so callers
 * can distinguish a genuine "not published" state from a fetch failure — but only while we
 * have never had a successful fetch. TanStack Query flips `status` to 'error' on ANY failed
 * fetch, including a background refetch, while `data` keeps its last successful value. Once
 * hasPublishedVersion has been established from real data, a later background-refetch error
 * must not make callers "forget" that known-good result and fail open again.
 */
export function useOfferHasPublishedVersion(offerId: string) {
  const auth = useEffectiveAuth();
  const query = useQuery(listVersionsOptions(auth, offerId));
  return {
    hasPublishedVersion: (query.data?.versions.length ?? 0) > 0,
    isLoading: query.isPending,
    isUnknownPublishStatus: query.isError && query.data === undefined,
  };
}
